import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, Code, Server, Network, Settings } from 'lucide-react';
import { vmTradingService } from '@/services/vmTradingService';
import { deploymentService } from '@/services/deploymentService';
import { getApiConfig } from '@/config/api';

interface EndpointStatus {
  endpoint: string;
  status: 'available' | 'missing' | 'error' | 'testing';
  response?: any;
  error?: string;
  httpStatus?: number;
}

export const VMEndpointTester: React.FC = () => {
  const [endpointStatuses, setEndpointStatuses] = useState<EndpointStatus[]>([]);
  const [isTestingAll, setIsTestingAll] = useState(false);
  const [vmDiscovery, setVmDiscovery] = useState<any>(null);
  
  // Get the configured VM URL from API config
  const apiConfig = getApiConfig();
  
  const [vmUrl, setVmUrl] = useState('https://stewies-api.stewietrader.com:8000'); // Updated: HTTPS server is now properly configured with SSL certificates
  const [showUrlConfig, setShowUrlConfig] = useState(false);

  const requiredEndpoints = [
    { endpoint: '/api/health', description: 'VM Health Check & Status', required: true },
    { endpoint: '/api/strategies/start', description: 'Deploy Strategy (POST)', required: true },
    { endpoint: '/api/strategies', description: 'Strategy Management', required: false },
    { endpoint: '/api/portfolio', description: 'Portfolio Data', required: false },
    { endpoint: '/api/orders', description: 'Orders Management', required: false },
    { endpoint: '/api/positions', description: 'Current Positions', required: false },
    { endpoint: '/api/market-data', description: 'Market Data', required: false },
  ];

  const testSingleEndpoint = async (endpoint: string): Promise<EndpointStatus> => {
    try {
      // Use the configured VM URL
      const baseUrl = vmUrl.replace(/\/+$/, ''); // Remove trailing slashes
      const fullUrl = `${baseUrl}${endpoint}`;
      console.log(`Testing VM endpoint: ${fullUrl}`);
      
      // Determine the appropriate HTTP method and body for each endpoint
      let method = 'GET';
      let body = undefined;
      
      if (endpoint === '/api/strategies/start') {
        method = 'POST';
        body = JSON.stringify({ 
          strategy_id: 'test_strategy',
          test_mode: true 
        });
      } else if (endpoint === '/api/orders' && endpoint.includes('/api/orders')) {
        // For orders endpoint, we'll test both GET (list orders) and the endpoint availability
        method = 'GET';
      }
      
      const response = await fetch(fullUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body,
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check for empty responses or "Not Found" messages
        if (data && typeof data === 'object') {
          if (data.detail === "Not Found" || data.error === "Not Found") {
            return {
              endpoint,
              status: 'missing',
              error: 'Endpoint returns "Not Found"',
              httpStatus: response.status,
            };
          }
          
          // Check for empty objects (like portfolio returning {})
          if (Object.keys(data).length === 0) {
            return {
              endpoint,
              status: 'available',
              response: { ...data, _note: 'Endpoint available but returns empty data' },
              httpStatus: response.status,
            };
          }
        }
        
        return {
          endpoint,
          status: 'available',
          response: data,
          httpStatus: response.status,
        };
      } else if (response.status === 404) {
        return {
          endpoint,
          status: 'missing',
          error: `Endpoint not found (HTTP 404)`,
          httpStatus: response.status,
        };
      } else if (response.status === 405) {
        // Method not allowed - try to detect what methods are supported
        const allowHeader = response.headers.get('Allow');
        return {
          endpoint,
          status: 'available',
          response: { 
            note: `Endpoint exists but ${method} method not allowed`,
            allowed_methods: allowHeader || 'Unknown',
            suggestion: endpoint === '/api/strategies/start' ? 'Use POST method' : 'Check method requirements'
          },
          httpStatus: response.status,
          error: `Method ${method} not allowed. Allowed: ${allowHeader || 'Unknown'}`
        };
      } else {
        return {
          endpoint,
          status: 'error',
          error: `HTTP ${response.status}: ${response.statusText}`,
          httpStatus: response.status,
        };
      }
    } catch (error) {
      // Check if it's a timeout or connection error
      if (error.name === 'AbortError') {
        return {
          endpoint,
          status: 'error',
          error: 'Request timed out (5s)',
        };
      }
      
      return {
        endpoint,
        status: 'missing',
        error: error.message || 'Connection failed',
      };
    }
  };

  const testAllEndpoints = async () => {
    setIsTestingAll(true);
    const results: EndpointStatus[] = [];

    // Set all to testing status first
    const testingStatuses = requiredEndpoints.map(ep => ({
      endpoint: ep.endpoint,
      status: 'testing' as const,
    }));
    setEndpointStatuses(testingStatuses);

    // Test each endpoint
    for (const { endpoint } of requiredEndpoints) {
      const result = await testSingleEndpoint(endpoint);
      results.push(result);
      
      // Update state incrementally so user sees progress
      setEndpointStatuses(prev => 
        prev.map(status => 
          status.endpoint === endpoint ? result : status
        )
      );
    }

    setIsTestingAll(false);
  };

  const discoverVMEndpoints = async () => {
    try {
      // Pass the current VM URL to the discovery service
      const discovery = await vmTradingService.discoverEndpoints(vmUrl);
      setVmDiscovery(discovery);
    } catch (error) {
      console.error('VM discovery failed:', error);
      setVmDiscovery({
        available: [],
        missing: ['All endpoints unavailable'],
        recommendations: [`Failed to connect to VM at ${vmUrl}. Error: ${error.message}`],
        vmUrl
      });
    }
  };

  const testVMServices = async () => {
    try {
      console.log('Testing VM trading services...');
      
      // Test account info
      const accountResult = await deploymentService.getAccountInfo();
      console.log('Account test result:', accountResult);
      
      // Test VM status check
      const vmStatus = await deploymentService.getVMStatus();
      console.log('VM status result:', vmStatus);
      
      alert('VM services test completed! Check console for details.');
    } catch (error) {
      console.error('VM services test failed:', error);
      alert(`VM services test failed: ${error.message}`);
    }
  };

  const getStatusIcon = (status: EndpointStatus['status']) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'missing':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'testing':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: EndpointStatus['status']) => {
    switch (status) {
      case 'available':
        return <Badge variant="default" className="bg-green-500">Available</Badge>;
      case 'missing':
        return <Badge variant="destructive">Missing</Badge>;
      case 'error':
        return <Badge variant="secondary">Error</Badge>;
      case 'testing':
        return <Badge variant="outline">Testing...</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-6 w-6" />
            Google VM Endpoint Tester
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={testAllEndpoints} disabled={isTestingAll}>
              {isTestingAll ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Testing Endpoints...
                </>
              ) : (
                <>
                  <Network className="h-4 w-4 mr-2" />
                  Test All Endpoints
                </>
              )}
            </Button>
            <Button variant="outline" onClick={discoverVMEndpoints}>
              <Code className="h-4 w-4 mr-2" />
              Discover Endpoints
            </Button>
            <Button variant="outline" onClick={testVMServices}>
              <Server className="h-4 w-4 mr-2" />
              Test VM Services
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowUrlConfig(!showUrlConfig)}
              className="ml-auto"
            >
              <Settings className="h-4 w-4 mr-2" />
              Configure VM URL
            </Button>
          </div>

          {showUrlConfig && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="vm-url">VM Base URL</Label>
                    <Input
                      id="vm-url"
                      type="url"
                      value={vmUrl}
                      onChange={(e) => setVmUrl(e.target.value)}
                      placeholder="https://your-vm-ip:8000"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Current: {vmUrl} {vmUrl === apiConfig.TRADING_BASE_URL ? '(from config)' : '(custom)'}
                    </p>
                  </div>
                  
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      <strong>Production Setup:</strong> Set <code>VITE_VM_API_BASE_URL</code> environment variable to your Google VM's external IP (e.g., <code>https://34.123.45.67:8000</code>)
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setVmUrl(apiConfig.TRADING_BASE_URL)}
                    >
                      Reset to Config
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setVmUrl('https://localhost:8000')}
                    >
                      Use Localhost
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {endpointStatuses.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Endpoint Status</h3>
                <div className="text-sm text-muted-foreground">
                  Testing VM: <code className="bg-gray-100 px-1 rounded">{vmUrl}</code>
                </div>
              </div>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-2 p-3 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">
                    {endpointStatuses.filter(s => s.status === 'available').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Available</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">
                    {endpointStatuses.filter(s => s.status === 'missing').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Missing</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-600">
                    {endpointStatuses.filter(s => s.status === 'error').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Errors</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {endpointStatuses.filter(s => s.status === 'testing').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Testing</div>
                </div>
              </div>
              {requiredEndpoints.map(({ endpoint, description, required }) => {
                const status = endpointStatuses.find(s => s.endpoint === endpoint);
                return (
                  <div key={endpoint} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status?.status || 'testing')}
                      <div>
                        <div className="font-medium">{endpoint}</div>
                        <div className="text-sm text-muted-foreground">{description}</div>
                        {required && <Badge variant="outline" className="text-xs">Required</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(status?.status || 'testing')}
                      {status?.httpStatus && (
                        <Badge variant="outline">{status.httpStatus}</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Action Items */}
              {endpointStatuses.filter(s => s.status === 'missing').length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">🔧 Action Required</h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    Your VM is running but missing {endpointStatuses.filter(s => s.status === 'missing').length} critical endpoints.
                    Add these endpoints to your VM server:
                  </p>
                  <div className="space-y-1">
                    {endpointStatuses
                      .filter(s => s.status === 'missing')
                      .map(status => (
                        <div key={status.endpoint} className="text-sm font-mono bg-yellow-100 px-2 py-1 rounded">
                          {status.endpoint} - {status.error}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
              
              {/* Success Message */}
              {endpointStatuses.filter(s => s.status === 'available').length >= 3 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">✅ VM Connected Successfully!</h4>
                  <p className="text-sm text-green-700">
                    Your VM at <code>{vmUrl}</code> is responding and has {endpointStatuses.filter(s => s.status === 'available').length} working endpoints.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {vmDiscovery && (
        <Card>
          <CardHeader>
            <CardTitle>VM Endpoint Discovery Results</CardTitle>
            <p className="text-sm text-muted-foreground">
              Tested VM URL: <code>{vmDiscovery.vmUrl}</code>
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-green-600">Available Endpoints:</h4>
              <ul className="list-disc list-inside space-y-1">
                {vmDiscovery.available.map((endpoint: string) => (
                  <li key={endpoint} className="text-sm">{endpoint}</li>
                ))}
              </ul>
            </div>

            {vmDiscovery.missing.length > 0 && (
              <div>
                <h4 className="font-semibold text-red-600">Missing Endpoints:</h4>
                <ul className="list-disc list-inside space-y-1">
                  {vmDiscovery.missing.map((endpoint: string) => (
                    <li key={endpoint} className="text-sm">{endpoint}</li>
                  ))}
                </ul>
              </div>
            )}

            {vmDiscovery.recommendations.length > 0 && (
              <div>
                <h4 className="font-semibold text-blue-600">Implementation Recommendations:</h4>
                {vmDiscovery.recommendations.map((rec: string, index: number) => (
                  <Alert key={index} className="mt-2">
                    <Code className="h-4 w-4" />
                    <AlertDescription>
                      <pre className="text-xs whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded mt-2">
                        {rec}
                      </pre>
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Quick VM Setup Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Server className="h-4 w-4" />
            <AlertDescription>
              <strong>Current VM Status:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1">
                <li>✅ <strong>/api/health</strong> - VM status and strategy count</li>
                <li>✅ <strong>/api/strategies/start</strong> - Deploy and run strategies</li>
                <li>❌ <strong>Trading endpoints</strong> - Orders, positions, portfolio (not implemented)</li>
              </ol>
              <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                <strong>Your VM works for strategy deployment!</strong> Additional endpoints would be needed for direct order management.
              </div>
            </AlertDescription>
          </Alert>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-semibold">Current VM Capabilities:</h4>
            <div className="text-sm space-y-1 text-green-700">
              <div>✅ <code>GET /api/health</code> - Shows active strategy count and VM status</div>
              <div>✅ <code>POST /api/strategies/start</code> - Deploy strategies with background execution</div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold">Optional Future Endpoints (if needed):</h4>
            <div className="text-sm space-y-1 text-gray-600">
              <div>📋 <code>GET /api/orders</code> - List orders (for manual trading)</div>
              <div>📋 <code>POST /api/orders</code> - Place orders (for manual trading)</div>
              <div>📋 <code>GET /api/positions</code> - View positions (for portfolio display)</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VMEndpointTester; 